import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useHistory } from 'react-router-dom';

import {
  TEACHERS_BULK_UPLOAD_ASSIGN_AND_CONFIRMATION_URL,
  TEACHERS_BULK_UPLOAD_SUBMISSIONS_MATCH_URL
} from 'config/urls';
import { get, isNil, some } from 'lodash';
import {
  bulkUploadAssignmentAssign,
  bulkUploadFirstAssignmentItems,
  bulkUploadPagesConfirm,
  bulkUploadPagesConfirmCancel,
  useAssignmentsNamesList,
  useBulkUploadDetail
} from 'sdk';

import Layout from 'pages/Teachers/BulkUpload/components/Layout';
import { useBulkUploadStatusRedirect } from 'pages/Teachers/BulkUpload/hooks';
import { useNavbar } from 'pages/Teachers/shared/TeacherPageLayout';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { notifyErrors } from 'utils/notifications';
import { reverse, stringifyParams } from 'utils/urls';

import AssignAssignment from './components/AssignAssignment';
import ConfirmPages from './components/ConfirmPages';
import AssignmentDialogContext from 'components/AssignmentDialogContext';
import UserContext from 'components/UserContext';

const ConsolidatedAssignAndConfirm = ({ match }) => {
  const [showLoader, setShowLoader] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [rotationCount, setRotationCount] = useState(0);
  const [firstAssignmentItems, setFirstAssignmentItems] = useState(null);
  const [focusedPageNumber, setFocusedPageNumber] = useState(1);
  const [focusedImage, setFocusedImage] = useState(null);
  const [isInitialAssignmentLoaded, setIsInitialAssignmentLoaded] =
    useState(false);
  const { setIsAssignmentDialogOpened } = useContext(AssignmentDialogContext);

  useNavbar({ title: 'CAPTURE WORK' });

  const pendingAssignmentData = localStorage.getItem(
    'pending_created_assignment'
  );

  const { data: assignmentsNames } = useAssignmentsNamesList({
    data: { include_prior_years: true }
  });

  const history = useHistory();

  const { user } = useContext(UserContext);

  const bulkUploadId = get(match, 'params.bulkUploadId');

  const {
    data: bulkUpload,
    isLoading: isFetchingBulkUpload,
    refetch: refectchBulkUploadDetails
  } = useBulkUploadDetail({ bulkUploadId });

  useBulkUploadStatusRedirect({
    bulkUpload,
    isLoading: isFetchingBulkUpload
  });

  const refetchAssignmentItems = useCallback(async () => {
    if (bulkUpload?.assignment) {
      const { data: assignmentItems } = await bulkUploadFirstAssignmentItems({
        bulkUploadId
      });
      setFirstAssignmentItems(assignmentItems);
    }
  }, [bulkUpload?.assignment, bulkUploadId]);

  useEffect(() => {
    async function fetchAssignmentData() {
      if (bulkUpload?.assignment) {
        const { data: assignmentItems } = await bulkUploadFirstAssignmentItems({
          bulkUploadId
        });
        setFirstAssignmentItems(assignmentItems);
      }
    }
    fetchAssignmentData();
  }, [bulkUpload, bulkUploadId, setFirstAssignmentItems]);

  const totalPageNumbers = firstAssignmentItems?.length;

  const shouldPoll = useMemo(() => {
    if (
      bulkUpload?.bulk_upload_images_have_been_successfully_created === false
    ) {
      return true;
    }

    if (
      bulkUpload?.assignment &&
      (!firstAssignmentItems || firstAssignmentItems.length === 0)
    ) {
      return true;
    }

    if (firstAssignmentItems && firstAssignmentItems.length > 0) {
      return some(firstAssignmentItems, (item) => isNil(item.image));
    }

    return false;
  }, [bulkUpload, firstAssignmentItems]);

  useEffect(() => {
    if (shouldPoll) {
      const interval = setInterval(() => {
        refetchAssignmentItems();
        refectchBulkUploadDetails();
      }, 1500);

      return () => clearInterval(interval);
    }
  }, [shouldPoll, refetchAssignmentItems, refectchBulkUploadDetails]);

  useEffect(() => {
    if (firstAssignmentItems) {
      setFocusedImage(firstAssignmentItems[focusedPageNumber - 1]);
    }
  }, [firstAssignmentItems, setFocusedImage, focusedPageNumber]);

  const removeAssociatedAssignment = useCallback(async () => {
    if (bulkUpload?.assignment) {
      setSelectedAssignment(null);
      setFocusedPageNumber(1);
      const { success, errors } = await bulkUploadPagesConfirmCancel({
        bulkUploadId
      });

      if (!success) {
        notifyErrors(errors);
      }
    }
  }, [bulkUpload?.assignment, bulkUploadId]);

  const handleAssignmentSelect = useCallback(
    async (value) => {
      setShowLoader(true);
      trackMixpanelEvent(
        user?.id,
        `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
        {
          feature: 'capture',
          view: 'assignment',
          button: 'assign',
          normalizedURL:
            process.env.REACT_APP_BASE_FRONTEND_URL +
            TEACHERS_BULK_UPLOAD_ASSIGN_AND_CONFIRMATION_URL
        }
      );

      if (!value) {
        setFirstAssignmentItems(null);
      } else if (
        bulkUpload?.assignment?.id !== value?.tracker_id &&
        !showLoader
      ) {
        await removeAssociatedAssignment();
        const { success, errors } = await bulkUploadAssignmentAssign({
          bulkUploadId,
          data: { assignment: value.tracker_id }
        });

        if (!success) {
          notifyErrors(errors);
        }
      }
      setSelectedAssignment(value);
      refectchBulkUploadDetails();

      setShowLoader(false);
    },
    [
      user?.id,
      bulkUpload?.assignment?.id,
      bulkUploadId,
      removeAssociatedAssignment,
      refectchBulkUploadDetails,
      showLoader
    ]
  );

  const handleInitialAssigning = useCallback(
    async (value) => {
      setShowLoader(true);
      trackMixpanelEvent(
        user?.id,
        `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
        {
          feature: 'capture',
          view: 'assignment',
          button: 'assign',
          normalizedURL:
            process.env.REACT_APP_BASE_FRONTEND_URL +
            TEACHERS_BULK_UPLOAD_ASSIGN_AND_CONFIRMATION_URL
        }
      );

      const { success, errors } = await bulkUploadAssignmentAssign({
        bulkUploadId,
        data: { assignment: value.tracker_id }
      });

      if (!success) {
        notifyErrors(errors);
      }

      setSelectedAssignment(value);
      refectchBulkUploadDetails();

      setShowLoader(false);
    },
    [user?.id, bulkUploadId, refectchBulkUploadDetails]
  );

  useEffect(() => {
    if (pendingAssignmentData && !showLoader) {
      try {
        const parsedAssignment = JSON.parse(pendingAssignmentData);
        handleInitialAssigning(parsedAssignment);
      } catch (error) {
        console.error('Failed to parse pending assignment:', error);
      } finally {
        localStorage.removeItem('pending_created_assignment');
      }
    }
  }, [handleInitialAssigning, showLoader, pendingAssignmentData]);

  useEffect(() => {
    if (
      bulkUpload?.assignment &&
      !selectedAssignment &&
      !isInitialAssignmentLoaded
    ) {
      setSelectedAssignment({
        tracker_id: bulkUpload.assignment.id,
        name: bulkUpload.assignment.name || 'Assigned Template'
      });
      setIsInitialAssignmentLoaded(true);
    }
  }, [
    bulkUpload?.assignment,
    assignmentsNames,
    selectedAssignment,
    isInitialAssignmentLoaded
  ]);

  const handleCreateClick = async () => {
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'capture',
        view: 'assignment',
        button: 'create',
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHERS_BULK_UPLOAD_ASSIGN_AND_CONFIRMATION_URL
      }
    );
    await removeAssociatedAssignment();
    setFirstAssignmentItems(null);
    refectchBulkUploadDetails();
    history.replace({
      pathname: history.pathname,
      search: stringifyParams({
        bulkUploadId,
        is_bulk_upload: true
      })
    });
    setIsAssignmentDialogOpened(true);
  };

  const handleNextClick = async ({ trackerId }) => {
    setShowLoader(true);
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`,
      {
        feature: 'capture',
        view: 'assignment',
        form: 'assignment',
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHERS_BULK_UPLOAD_SUBMISSIONS_MATCH_URL
      }
    );

    const { success, errors } = await bulkUploadPagesConfirm({
      bulkUploadId,
      rotationCount
    });

    if (success) {
      history.push(
        reverse(TEACHERS_BULK_UPLOAD_SUBMISSIONS_MATCH_URL, {
          bulkUploadId: bulkUploadId
        })
      );
    } else {
      notifyErrors(errors);
    }

    setShowLoader(false);
  };

  const isProcessing =
    bulkUpload?.bulk_upload_images_have_been_successfully_created === false;
  const isPageLoading = isFetchingBulkUpload || isProcessing || showLoader;

  return (
    <>
      <Layout
        currentStepIndex={1}
        showLoader={isPageLoading}
        nextButtonAction={handleNextClick}
        isNextButtonDisabled={
          isNil(firstAssignmentItems) || isPageLoading || !selectedAssignment
        }
      >
        <AssignAssignment
          selectedAssignment={selectedAssignment}
          assignmentsNames={assignmentsNames}
          handleAssignmentSelect={handleAssignmentSelect}
          handleCreateClick={handleCreateClick}
        />
        {!isPageLoading && firstAssignmentItems && selectedAssignment && (
          <ConfirmPages
            bulkUpload={bulkUpload}
            focusedImage={focusedImage}
            rotationCount={rotationCount}
            focusedPageNumber={focusedPageNumber}
            setFocusedPageNumber={setFocusedPageNumber}
            totalPageNumbers={totalPageNumbers}
            setRotationCount={setRotationCount}
            user={user}
          />
        )}
      </Layout>
    </>
  );
};

export default ConsolidatedAssignAndConfirm;
